import { useState, useEffect, useCallback } from 'react';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { Box, Chip, Stack } from '@mui/material';
import { NotificationsActive } from '@mui/icons-material';
import { useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

// components
import Scrollbar from '../../components/scrollbar';

// services
import Loading from '../../layouts/loading';
import { AddSection, Section } from './boardview/components';
import { changeSectionOrder, getSectionsByProjectId } from '../../services/sectionService';
import BoardSearchField from '../../components/board-search';
import { getProjectLabels } from '../../services/projectLabelService';
import { fetchRunningTask } from '../../services/trackerService';
import { useProject } from './Layout/header/context';
import { isClient, isStakeholder } from '../../utils/share';

// ----------------------------------------------------------------------

function ProjectBoard() {
  const { id } = useParams();
  const [isLoading, setLoading] = useState(false);
  const [sections, setSections] = useState([]);
  const [refetcher, setRefetcher] = useState(false);
  const [runningTask, setRunningTask] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  // A shared task link is ?task=<id>. The board already holds every section's tasks,
  // so the section owning the task can open its own drawer with no extra fetch.
  const deepLinkTaskId = searchParams.get('task');
  const [boardQuery, setBoardQuery] = useState('');
  const [labels, setLabels] = useState([]);
  const [activeLabelIds, setActiveLabelIds] = useState([]);
  const [reminderOnly, setReminderOnly] = useState(false);

  const { projectDetails, isEditPermission } = useProject();

  const fetchSectionsSilent = useCallback(async () => {
    try {
      const response = await getSectionsByProjectId(id);
      setSections(response?.data?.data?.rows);
    } catch (error) {
      toast.error(error.message);
    }
  }, [id]);

  const fetchSections = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getSectionsByProjectId(id);
      setSections(response?.data?.data?.rows);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  }, [id]);

  const refetchSection = useCallback(
    (showLoader = true) => {
      if (showLoader) {
        setRefetcher((prev) => !prev);
      } else {
        // Silent refetch without page loader
        fetchSectionsSilent();
      }
    },
    [fetchSectionsSilent]
  );

  const fetchLabels = useCallback(async () => {
    try {
      const response = await getProjectLabels(id);
      setLabels(response?.data?.data || []);
    } catch (error) {
      // A failed label fetch must not stop the board from rendering.
      setLabels([]);
    }
  }, [id]);

  // Labels toggle independently, so several can be active at once. Clicking an active
  // one removes it from the selection.
  const handleLabelClick = useCallback((labelId) => {
    setActiveLabelIds((current) =>
      current.includes(labelId) ? current.filter((id) => id !== labelId) : [...current, labelId]
    );
  }, []);

  const fetchRunningTaskData = async () => {
    const runningTaskResponse = await fetchRunningTask();
    setRunningTask(runningTaskResponse?.data?.data);
  };

  useEffect(() => {
    fetchSections();
    fetchRunningTaskData();
    fetchLabels();
  }, [fetchSections, refetcher, fetchLabels]);

  // Each user arranges the board for themselves, so every member may reorder.
  const onDragEnd = useCallback(
    async (result) => {
      const { source, destination } = result;
      if (!destination || destination.index === source.index) return;

      const previousSections = sections;
      const destinationOrder = sections[destination.index]?.order;
      if (typeof destinationOrder !== 'number') {
        toast.error('Something went wrong while reordering the sections');
        return;
      }

      const reorderedSections = Array.from(sections);
      const [movedSection] = reorderedSections.splice(source.index, 1);
      reorderedSections.splice(destination.index, 0, movedSection);
      setSections(reorderedSections);

      try {
        await changeSectionOrder({
          projectId: Number(id),
          sectionId: movedSection?.id,
          destinationOrder,
        });
      } catch (error) {
        setSections(previousSections);
        const errMsg =
          error?.response?.data?.message || 'An error occurred while reordering the sections. Please try again later.';
        toast.error(errMsg);
      }
    },
    [sections, id]
  );

  const clearDeepLink = useCallback(() => {
    setSearchParams(
      (params) => {
        params.delete('task');
        return params;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const setDeepLink = useCallback(
    (taskId) => {
      setSearchParams(
        (params) => {
          if (taskId) params.set('task', String(taskId));
          else params.delete('task');
          return params;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  // A link pointing at the wrong project, or at a task that has since moved, would
  // otherwise sit in the URL opening nothing.
  useEffect(() => {
    if (!deepLinkTaskId || !sections.length) return;
    const found = sections.some((section) =>
      (section?.tasks || []).some((task) => String(task?.id) === String(deepLinkTaskId))
    );
    if (!found) {
      toast.error('That task is not in this project');
      clearDeepLink();
    }
  }, [deepLinkTaskId, sections, clearDeepLink]);

  const createTaskDisabled = projectDetails?.allowManualTask === false && !isEditPermission;

  // Filter sections for stakeholders - only show "Issues" or "issue" 

  return (
    <>
      <DndProvider backend={HTML5Backend}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }} gap={2}>
          <Stack direction="row" spacing={1} flexWrap="wrap" alignItems="center" useFlexGap>
            {labels.map((label) => {
              const isActive = activeLabelIds.includes(label.id);
              return (
                <Chip
                  key={label.id}
                  size="small"
                  label={label.name}
                  onClick={() => handleLabelClick(label.id)}
                  sx={{
                    fontWeight: 600,
                    cursor: 'pointer',
                    height: 26,
                    background: isActive ? label.color : 'transparent',
                    color: isActive ? '#fff' : label.color,
                    border: `1px solid ${label.color}`,
                    '&:hover': { background: isActive ? label.color : `${label.color}22` },
                    // The app's global line-height pushed the chip text off-centre; pin it
                    // to the chip's own box instead.
                    '& .MuiChip-label': { lineHeight: 1, px: 1.25, display: 'flex', alignItems: 'center' },
                  }}
                />
              );
            })}
            <Chip
              size="small"
              icon={<NotificationsActive sx={{ fontSize: 16 }} />}
              label="Has reminder"
              onClick={() => setReminderOnly((current) => !current)}
              sx={{
                fontWeight: 600,
                cursor: 'pointer',
                height: 26,
                background: reminderOnly ? '#f0a500' : 'transparent',
                color: reminderOnly ? '#fff' : '#b37b00',
                border: '1px solid #f0a500',
                '& .MuiChip-icon': { color: reminderOnly ? '#fff' : '#b37b00' },
                '& .MuiChip-label': { lineHeight: 1, display: 'flex', alignItems: 'center' },
                '&:hover': { background: reminderOnly ? '#f0a500' : '#f0a50022' },
              }}
            />
            {(activeLabelIds.length > 0 || reminderOnly) && (
              <Chip
                size="small"
                variant="outlined"
                label="Clear"
                onDelete={() => {
                  setActiveLabelIds([]);
                  setReminderOnly(false);
                }}
                onClick={() => {
                  setActiveLabelIds([]);
                  setReminderOnly(false);
                }}
                sx={{
                  cursor: 'pointer',
                  color: 'text.secondary',
                  height: 26,
                  '& .MuiChip-label': { lineHeight: 1, display: 'flex', alignItems: 'center' },
                }}
              />
            )}
          </Stack>
          <BoardSearchField
            value={boardQuery}
            onChange={setBoardQuery}
            placeholder="Search tasks in this board"
          />
        </Stack>
        <Scrollbar containerProps={{ style: { height: 'calc(100% - 100px)' } }} sx={{ height: '100%' }}>
          <Stack direction="row" spacing={2} alignItems="flex-start">
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="sections" direction="horizontal">
                {(droppableProvided) => (
                  <Stack
                    direction="row"
                    spacing={2}
                    ref={droppableProvided.innerRef}
                    {...droppableProvided.droppableProps}
                  >
                    {sections.map((section, index) => (
                      <Draggable key={section?.id} draggableId={String(section?.id)} index={index}>
                        {(draggableProvided) => (
                          <Box ref={draggableProvided.innerRef} {...draggableProvided.draggableProps}>
                            <Section
                              section={section}
                              refetchSection={refetchSection}
                              setLoading={setLoading}
                              runningTask={runningTask}
                              dragHandleProps={draggableProvided.dragHandleProps}
                              deepLinkTaskId={deepLinkTaskId}
                              onTaskOpenChange={setDeepLink}
                              activeLabelIds={activeLabelIds}
                              reminderOnly={reminderOnly}
                              onLabelClick={handleLabelClick}
                              boardQuery={boardQuery}
                            />
                          </Box>
                        )}
                      </Draggable>
                    ))}
                    {droppableProvided.placeholder}
                  </Stack>
                )}
              </Droppable>
            </DragDropContext>
            {!createTaskDisabled && <AddSection refetchSection={refetchSection} />}
          </Stack>
        </Scrollbar>
        <Loading open={isLoading} />
      </DndProvider>
    </>
  );
}
export default ProjectBoard;
